import { fmtPrice, t, market as resolveMarket } from "@/lib/terms";

// Shared "For Sale / Sold STC / Let" badge logic for public property cards.
// Mirrors the status filter widened in crm/public_routes.py — sold/let
// listings stay visible on the public site (never on the portal-feed
// export) so the site shows real, credible market activity.
//
// `market` ("uk" | "us", default "uk") localises the label — e.g. a US
// tenant shows "Under contract" / "Lease pending" / "Leased" instead of
// "Sold STC" / "Let Agreed" / "Let".
export function statusBadge(status, listingType, market = "uk") {
  switch (status) {
    case "under_offer":
      return {
        label: listingType === "letting" ? t("let_agreed", market) : t("sold_stc", market),
        tone: "warn",
      };
    case "sold":
      return { label: t("sold", market), tone: "muted" };
    case "let":
      return { label: t("let_done", market), tone: "muted" };
    default:
      return null;
  }
}

// THE price-qualifier list for the whole web app — the CRM's own dropdown
// (CrmProperties.jsx) imports it from here rather than keeping a second copy.
// The values are the canonical enumeration, PriceQualifier in
// backend/crm/models.py, and PRICE_QUALIFIER_SHORT in backend/crm/utils.py
// carries the same wording for the brochure. Both have to stay identical, or
// the screen and the printed page disagree about the same listing. `label`
// is the fuller wording the dropdown shows, `short` the inline form that
// sits in front of a price. UK-only — a US listing is priced by its list
// price and the MLS takes no qualifier.
export const PRICE_QUALIFIERS = [
  { value: "", label: "None — just the price", short: "" },
  { value: "guide_price", label: "Guide price", short: "Guide" },
  { value: "fixed_price", label: "Fixed price", short: "Fixed" },
  { value: "offers_over", label: "Offers over", short: "Offers over" },
  { value: "offers_in_excess_of", label: "Offers in excess of (OIEO)", short: "OIEO" },
  { value: "oiro", label: "Offers in the region of (OIRO)", short: "OIRO" },
  { value: "sale_by_tender", label: "Sale by tender", short: "Tender" },
  { value: "poa", label: "Price on application (POA)", short: "POA" },
  { value: "from", label: "From", short: "From" },
  { value: "shared_ownership", label: "Shared ownership", short: "Shared ownership" },
  { value: "part_buy_part_rent", label: "Part buy, part rent", short: "Part buy, part rent" },
  { value: "shared_equity", label: "Shared equity", short: "Shared equity" },
];

// Short form keyed by stored value, for display next to a price.
export const PRICE_QUALIFIER_SHORT = Object.fromEntries(
  PRICE_QUALIFIERS.filter((q) => q.value).map((q) => [q.value, q.short]),
);

// The property-type vocabulary, per market.
//
// The UK list is the RTDF property_type enum the UK portals took (see the
// note in CrmProperties.jsx: free text was accepted by the feed and then
// displayed under a generic type, which is how "Semi-Detached Cottage" got
// published).
// It is UK housing stock described in UK words, and almost none of it means
// anything to an American: there are no maisonettes, "terraced" is a
// townhouse, and a bungalow is a style rather than a category.
//
// The US list is the RESO Property Sub Type vocabulary every MLS keys from,
// so what an agent picks here is what their MLS expects. Both lists store
// the same field; only the options differ.
export const PROPERTY_TYPES_UK = [
  { value: "Detached", label: "Detached house" },
  { value: "Semi-Detached", label: "Semi-detached house" },
  { value: "Terraced", label: "Terraced house" },
  { value: "End Terrace", label: "End of terrace" },
  { value: "Town House", label: "Town house" },
  { value: "Link Detached", label: "Link-detached house" },
  { value: "Mews", label: "Mews" },
  { value: "Cottage", label: "Cottage" },
  { value: "Country House", label: "Country house" },
  { value: "Coach House", label: "Coach house" },
  { value: "Barn Conversion", label: "Barn conversion" },
  { value: "Farmhouse", label: "Farmhouse" },
  { value: "Bungalow", label: "Bungalow" },
  { value: "Detached Bungalow", label: "Detached bungalow" },
  { value: "Semi-Detached Bungalow", label: "Semi-detached bungalow" },
  { value: "Terraced Bungalow", label: "Terraced bungalow" },
  { value: "Flat", label: "Flat / apartment" },
  { value: "Maisonette", label: "Maisonette" },
  { value: "Studio", label: "Studio" },
  { value: "Block Of Flats", label: "Block of flats" },
  { value: "Park Home", label: "Park home" },
  { value: "Houseboat", label: "Houseboat" },
  { value: "Lodge", label: "Lodge" },
  { value: "Chalet", label: "Chalet" },
  { value: "Villa", label: "Villa" },
  { value: "Equestrian", label: "Equestrian" },
  { value: "Farm", label: "Farm" },
  { value: "Land", label: "Land" },
  { value: "Parking", label: "Parking" },
];

export const PROPERTY_TYPES_US = [
  { value: "SingleFamilyResidence", label: "Single family residence" },
  { value: "Condominium", label: "Condominium" },
  { value: "Townhouse", label: "Townhouse" },
  { value: "Duplex", label: "Duplex" },
  { value: "Triplex", label: "Triplex" },
  { value: "Quadruplex", label: "Fourplex" },
  { value: "MultiFamily", label: "Multi-family (5+ units)" },
  { value: "Apartment", label: "Apartment" },
  { value: "Stock Cooperative", label: "Co-op" },
  { value: "ManufacturedHome", label: "Manufactured home" },
  { value: "ManufacturedOnLand", label: "Manufactured home on land" },
  { value: "MobileHome", label: "Mobile home" },
  { value: "Cabin", label: "Cabin" },
  { value: "Farm", label: "Farm" },
  { value: "Ranch", label: "Ranch" },
  { value: "UnimprovedLand", label: "Land / lot" },
  { value: "Timeshare", label: "Timeshare" },
  { value: "OwnYourOwn", label: "Own-your-own" },
  { value: "Other", label: "Other" },
];

export function propertyTypes(market = "uk") {
  return resolveMarket(market) === "us" ? PROPERTY_TYPES_US : PROPERTY_TYPES_UK;
}

/**
 * A stored type, as words.
 *
 * The value stored is the RESO enum, which is correct: it is the MLS's own
 * vocabulary and the feed expects it. Printing it is not, and the property
 * header read "SingleFamilyResidence" next to the price. Splitting the
 * CamelCase handles anything not in the list, so a type the MLS adds
 * tomorrow still reads as words rather than as a database value.
 */
export function propertyTypeLabel(value, market = "uk") {
  if (!value) return "";
  const known = [...PROPERTY_TYPES_US, ...PROPERTY_TYPES_UK]
    .find((t) => t.value === value);
  if (known) return known.label;
  return String(value)
    .replace(/(?<=[a-z0-9])(?=[A-Z])/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * How a home is owned. The UK stores "Freehold" or "Leasehold"; the US
 * equivalent distinction is fee simple against a condo or co-op interest,
 * and it is the HOA that a US buyer actually asks about.
 */
export const TENURES_UK = ["Freehold", "Leasehold", "Share of Freehold", "Commonhold"];
export const TENURES_US = ["Fee Simple", "Condominium", "Co-op", "Leasehold", "Land Lease"];

export function tenures(market = "uk") {
  return resolveMarket(market) === "us" ? TENURES_US : TENURES_UK;
}

/** The sensible default for a new record in each market. */
export function defaultTenure(market = "uk") {
  return resolveMarket(market) === "us" ? "Fee Simple" : "Freehold";
}

export function fmtListingPrice(p, market = "uk") {
  const raw = Number(p.listing_type === "letting" ? p.rental_pcm : p.asking_price) || 0;
  // Price qualifiers are a UK concept only.
  const qualifier = resolveMarket(market) === "us" ? null : PRICE_QUALIFIER_SHORT[p.price_qualifier];
  // Nothing priced yet (an appraisal not taken to instruction), or a genuine
  // POA listing — "£0" reads as free rather than "no figure on file".
  if (!raw) return qualifier === "POA" ? "POA" : "—";
  const amount = p.listing_type === "letting"
    ? fmtPrice(raw, market, { rent: true })
    : fmtPrice(raw, market);
  return qualifier ? `${qualifier} ${amount}` : amount;
}
